import { useCallback, useMemo, useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import { CircleCheckIcon, CodeIcon, CopyIcon, ImportIcon } from 'lucide-react'
import { toast } from 'sonner'

import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import { FormActions } from '@/components/form-actions.tsx'
import { JSONSchemaBuilder } from '@/components/json-schema-generator/json-schema-builder.tsx'
import { PropertySchema } from '@/components/json-schema-generator/types.ts'
import { PageTitleActions } from '@/components/layout/page-title'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { SubmitButton } from '@/components/submit-button.tsx'
import { useCreateAttribute } from '@/features/attributes/hooks/use-create-attribute.ts'
import { useUpdateAttributeById } from '@/features/attributes/hooks/use-update-attribute.ts'
import { Attribute } from '@/features/attributes/types.ts'

interface AttributeFormProps {
  attributeId?: string
  attribute?: Attribute
  onSubmitted?: (attribute: Attribute) => void
}

export function AttributeForm(props: AttributeFormProps) {
  const { attributeId, attribute, onSubmitted } = props
  const router = useRouter()
  const [showJsonDialog, setShowJsonDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [importJson, setImportJson] = useState('')

  // Separate state for JSONSchemaBuilder to avoid circular dependencies
  const [builderSchema, setBuilderSchema] = useState<PropertySchema>(() => {
    return {
      type: 'object',
      properties: {
        ...(attribute?.name
          ? {
              [attribute.name]: attribute?.json_schema,
            }
          : { '': { type: 'string' } }),
      },
      ...(attribute?.required ? { required: [attribute?.name] } : {}),
    } as PropertySchema
  })

  const form = useSmartForm<Attribute>({
    values: attribute as Attribute,
  })

  const createAttributeMutation = useCreateAttribute()
  const updateAttributeMutation = useUpdateAttributeById()

  const onSubmit = async (data: Attribute) => {
    if (attributeId) {
      const result = await updateAttributeMutation.mutateAsync({
        attributeId,
        data,
      })

      toast('Attributo salvato', { icon: <CircleCheckIcon /> })

      return onSubmitted?.(result.data as Attribute)
    }

    const result = await createAttributeMutation.mutateAsync(data)
    toast('Attributo creato', { icon: <CircleCheckIcon /> })

    return onSubmitted?.(result.data as Attribute)
  }

  const handleJsonSchemaBuilderChange = useCallback(
    (schema: PropertySchema) => {
      // Update form values without causing infinite loop
      // @ts-expect-error TODO
      form.setValue('json_schema', schema, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      // Update required field
      const currentName = schema.$id || form.getValues('name')
      form.setValue('required', !!schema.required?.includes(currentName), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      // Update name field
      if (schema.$id) {
        form.setValue('name', schema.$id, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }
    },
    [form]
  )

  const handleImportJson = () => {
    try {
      const parsedSchema = JSON.parse(importJson)

      // Handle different schema formats
      const schemaName =
        parsedSchema.$id || parsedSchema.name || 'imported_field'
      const schemaData = { ...parsedSchema }

      // Remove $id and name from the property data since they'll be used as keys
      delete schemaData.$id
      delete schemaData.name

      // Create the new schema for the builder
      const newBuilderSchema = {
        type: 'object' as const,
        properties: {
          [schemaName]: schemaData,
        },
        required: Array.isArray(parsedSchema.required)
          ? parsedSchema.required.filter((req: string) => req === schemaName)
          : parsedSchema.required === true ||
              (parsedSchema.$id &&
                parsedSchema.required?.includes?.(parsedSchema.$id))
            ? [schemaName]
            : [],
      }

      // Update builder schema state
      setBuilderSchema(newBuilderSchema)

      // Convert for form update
      const convertedSchema = {
        $id: schemaName,
        ...schemaData,
        required: newBuilderSchema.required || [],
      }

      // Update the form with the imported schema
      handleJsonSchemaBuilderChange(convertedSchema as PropertySchema)

      setImportJson('')
      setShowImportDialog(false)
      toast('Schema importato con successo!')
    } catch (error) {
      console.error('Import error:', error)
      toast("Errore nell'importazione: JSON non valido", {
        description: 'Verifica che il JSON sia correttamente formattato.',
      })
    }
  }

  const currentJsonSchema = useMemo(() => {
    const propName = Object.keys(builderSchema.properties ?? {})[0]
    return {
      $id: propName,
      ...(builderSchema.properties?.[propName] as PropertySchema),
      required: builderSchema.required || [],
    }
  }, [builderSchema])

  const handleCopyJsonSchema = () => {
    const jsonString = JSON.stringify(currentJsonSchema, null, 2)
    navigator.clipboard.writeText(jsonString)
    toast('Schema JSON copiato negli appunti!')
  }

  return (
    <>
      <ScrollArea className="-mx-1 h-full p-4">
        <Form {...form}>
          <div className="mb-4 flex flex-row items-start justify-between gap-2 md:items-center">
            <PageTitle className="mb-0 min-w-0 flex-1">
              {attributeId ? (
                <>Modifica Attributo: {attribute?.name}</>
              ) : (
                <>Nuovo Attributo</>
              )}
            </PageTitle>
            <PageTitleActions>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowJsonDialog(true)}
              >
                <CodeIcon className="size-4" />
                Mostra JSON Schema
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowImportDialog(true)}
              >
                <ImportIcon className="size-4" />
                Importa JSON Schema
              </Button>
            </PageTitleActions>
          </div>

          {/* Dialogs are always mounted here */}
          <Dialog open={showJsonDialog} onOpenChange={setShowJsonDialog}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>JSON Schema Generato</DialogTitle>
                <DialogDescription>
                  Questo è lo schema JSON generato dal form corrente
                </DialogDescription>
              </DialogHeader>
              <div className="relative">
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-gray-100 p-4 pr-12 text-sm">
                  {JSON.stringify(currentJsonSchema, null, 2)}
                </pre>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyJsonSchema}
                  className="absolute right-2 top-2 size-8 bg-white/80 p-0 backdrop-blur-sm hover:bg-white"
                >
                  <CopyIcon className="size-3" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Importa JSON Schema</DialogTitle>
                <DialogDescription>
                  Incolla un JSON schema per importare la configurazione del
                  campo
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Textarea
                  value={importJson}
                  onChange={(e) => setImportJson(e.target.value)}
                  placeholder='Incolla qui il JSON del schema (es: {"$id": "nome_campo", "type": "string", ...})'
                  rows={10}
                  className="font-mono text-sm"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportJson('')
                      setShowImportDialog(false)
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleImportJson}
                    disabled={!importJson.trim()}
                  >
                    Importa Schema
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="max-w-screen-sm space-y-8 px-1">
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              {/*{JSON.stringify(form.watch().json_schema)}*/}
              {/*isDirty: {JSON.stringify(form.formState.isDirty)}*/}
              <JSONSchemaBuilder
                schema={builderSchema as PropertySchema}
                onChange={handleJsonSchemaBuilderChange}
              />
              <div>
                <Separator className="my-10" />
              </div>
              <fieldset className="space-y-8">
                <h3 className="mb-4 text-lg font-medium">
                  Opzioni di visualizzatione
                </h3>

                <FormField
                  control={form.control}
                  name="hidden_in_list"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Nascondi nelle liste</FormLabel>
                        <FormDescription>
                          Se attivato, il campo non sarà visibile nella lista
                          dei documenti. <br />
                          Il campo rimmarrà visibile nella pagina dei dettagli
                          del documento.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hidden"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Sempre nascosto</FormLabel>
                        <FormDescription>
                          Il campo non verrà mai mostrato, neanche nella pagina
                          dei dettagli di un documento. <br />
                          Ha priorità su "Non mostrare nella lista", rendendo
                          invisibile il campo ovunque. <br />
                          Utile per campi usati solo per la ricerca o come
                          metadati invisibili all'utente.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </fieldset>

              <FormActions>
                <Button
                  type="reset"
                  variant="outline"
                  size="lg"
                  onClick={() =>
                    attributeId ? form.reset() : router.history.back()
                  }
                >
                  {attributeId ? 'Reset' : 'Annulla'}
                </Button>
                <SubmitButton
                  size="lg"
                  isSubmitting={form.formState.isSubmitting}
                  isDirty={form.formState.isDirty}
                >
                  Salva
                </SubmitButton>
              </FormActions>
            </form>
          </div>
        </Form>
      </ScrollArea>
    </>
  )
}
